<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import {
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
  iFeaturedIcon,
  iFeaturedIconSizes,
} from '@inscribe/signal';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import { Statuses } from '@/V2/components/status/utils';
import { mapActions, mapGetters } from 'vuex';
import { riskLevelToReadableLabel } from '@/constants/riskLevels';
import DocumentCard, {
  DocumentCardSizes,
} from '@/V2/components/documents/DocumentCard.vue';
import CustomerEmptyState from '@/V2/customers/CustomerEmptyState.vue';
import { $event } from '@/bus';

export default {
  components: {
    iButton,
    iFeaturedIcon,
    ExpandableContainer,
    StatusBadge,
    DocumentCard,
    CustomerEmptyState,
  },
  props: {
    riskLevel: {
      type: String,
    },
    fraudSummary: {
      type: Object,
    },
  },
  data() {
    return {
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      iFeaturedIconSizes,
      Statuses,
      displayedDocuments: [],
      isLoadingDocuments: true,
      DocumentCardSizes,
    };
  },
  computed: {
    ...mapGetters(['someDocumentUploadingOrProcessing']),
    fraudSummaryTotal() {
      return Object.values(
        this.fraudSummary?.aggregated_risk_counts || {},
      ).reduce((acc, curr) => acc + curr, 0);
    },
    subtitle() {
      let count = '';
      let currentRiskCount =
        this.fraudSummary?.aggregated_risk_counts?.[this.riskLevel];
      if (currentRiskCount) {
        count = `• ${this.fraudSummaryTotal} total`;
      }

      return this.riskLevel
        ? `${currentRiskCount} ${riskLevelToReadableLabel(this.riskLevel).replace(/\s+/g, '-').toLowerCase()} ${currentRiskCount === 1 ? 'document' : 'documents'} ${count}`
        : this.isLoadingDocuments
          ? 'Loading...'
          : 'No documents';
    },
    showEmptyState() {
      return !this.displayedDocuments.length;
    },
    returnToPath() {
      return this.$route.fullPath;
    },
  },
  watch: {
    riskLevel: {
      handler() {
        this.loadDocuments();
      },
    },
  },
  mounted() {
    $event.$on('customer-review-agent-status-updated', (pusherData) => {
      if (
        this.$route.params.customer_id === pusherData.customer_uuid.toString()
      ) {
        this.loadDocuments();
      }
    });

    this.loadDocuments();
  },
  methods: {
    ...mapActions(['getDocumentsForCustomer']),
    loadDocuments() {
      this.isLoadingDocuments = true;
      this.displayedDocuments = [];
      if (!this.riskLevel) {
        this.isLoadingDocuments = false;
        return;
      }

      this.getDocumentsForCustomer({
        customerId: this.$route.params.customer_id,
        queryParams: {
          risk_levels: [this.riskLevel],
        },
      })
        .then((response) => {
          this.displayedDocuments = response?.data?.data || [];
        })
        .finally(() => {
          this.isLoadingDocuments = false;
        });
    },
    onDocumentClick(document) {
      this.$router
        .push({
          name: 'document-signals',
          params: { document_id: document.id },
          query: {
            return_to: this.returnToPath,
          },
        })
        .catch(() => {});
    },
    onViewAllClick() {
      this.$router
        .push({
          name: 'customer-documents',
          params: { customer_id: this.$route.params.customer_id },
        })
        .catch(() => {});
    },
    onUploadDocumentsClick() {
      this.$emit('upload-documents');
    },
  },
};
</script>

<template>
  <div class="customer-fraud-documents">
    <ExpandableContainer title="Documents" :subtitle="subtitle">
      <template #tools>
        <i-button
          v-if="!showEmptyState"
          text="View All"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.OUTLINE"
          @button-click="onViewAllClick"
        />
      </template>
      <template #items>
        <div class="documents-list d-flex flex-column">
          <div
            v-if="isLoadingDocuments"
            class="empty-list d-flex flex-column"
            data-testid="customer-fraud-documents-loading"
          >
            <document-card
              v-for="i in 3"
              :key="i"
              :loading="true"
              :size="DocumentCardSizes.SMALL"
            ></document-card>
          </div>

          <customer-empty-state
            v-else-if="showEmptyState"
            title="No Documents Analyzed"
            description="Upload documents to begin analysis"
            icon="medium-file"
            @button-click="onUploadDocumentsClick"
            data-testid="customer-fraud-documents-empty-state"
          />

          <template v-else>
            <document-card
              v-for="document in displayedDocuments"
              :key="document.id"
              :document="document"
              :size="DocumentCardSizes.SMALL"
              :return-to="returnToPath"
              @on-document-click="onDocumentClick"
            ></document-card>
          </template>
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.documents-list {
  border: 0;
  gap: 1px;

  .empty-list {
    width: 100%;
    gap: 1px;

    .expandable-item {
      width: 100%;
    }
  }

  .document-card {
    border-radius: 0;
    border: 0;
  }
}
</style>
